import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ColumnFiltersState,
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  Row,
  useReactTable,
} from '@tanstack/react-table';

import {
  dateFormatter,
  timeFormatter,
} from '../../../utils/functions/common-utils';
import {
  getDefaultExpandedState,
  nestAdBoardDataFeedByThread,
} from '../../../utils/functions/format-feed-data';
import i18n from '../../../utils/i18n';
import {
  AdBoardOutletContextType,
  AdBoardSectionDataFeedDto,
  AdBoardSectionDataFeedDtoWithSubRows,
  AdBoardSectionGroupDto,
  SectionDto,
} from '../types';

import { FeedContentCell } from './FeedContentCell';
import { ParticipantNameCell } from './ParticipantNameCell';
import { SubRowCell } from './SubRowCell';

const createColumns = (
  filterByUserId?: string,
  selectedMessageId?: string,
  selectedActivity?: SectionDto
) => {
  const columnHelper =
    createColumnHelper<AdBoardSectionDataFeedDtoWithSubRows>();
  return [
    columnHelper.accessor('userId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'User id',
      // Custom filter for userId column
      filterFn: (row, columnId, filterValue) => {
        const userId = row.getValue(columnId);
        return userId === Number(filterValue); // Compare after converting filterValue to a number
      },
    }),
    columnHelper.accessor('participantName', {
      cell: (info) =>
        info.row.depth < 1 && (
          <ParticipantNameCell
            filterByUserId={!!filterByUserId}
            participantName={
              info.row.depth > 0
                ? info.row.original.participantName
                : info.getValue()
            }
            createdAt={info.row.original.createdAt.toString()}
            userId={info.row.original.userId.toString()}
          />
        ),
      size: 160,
      header: 'Participant name',
    }),
    columnHelper.accessor('content', {
      cell: (info) => {
        const commonProps = {
          context: info.row.original.messageContext,
          sectionType: info.row.original.sectionType,
          attachmentName: info.row.original.attachmentName,
          attachmentOpenedAt: info.row.original.attachmentOpenedAt,
          content: info.getValue(),
          editedAt: info.row.original.editedAt,
          hidden: info.row.original.hidden,
          threadId: info.row.original.threadId,
          messageId: info.row.original.messageId,
          parentMessageContent: info.row.original.parentMessageContent,
          getCanExpand: info.row.getCanExpand,
          getIsExpanded: info.row.getIsExpanded,
          toggleExpanded: info.row.toggleExpanded,
          subRowCount: info.row.original.subRows?.length ?? 0,
          selectedActivity,
          selectedMessageId,
          viewThreadModal: !!selectedMessageId,
          participantView: !!filterByUserId,
          isGeneralDiscussion: false,
        };
        return info.row.depth > 0 ? (
          <SubRowCell
            participantName={
              info.row.depth > 0
                ? info.row.original.participantName
                : info.getValue() ?? ''
            }
            createdAt={info.row.original.createdAt.toString()}
            userId={info.row.original.userId.toString()}
            {...commonProps}
          />
        ) : (
          <FeedContentCell {...commonProps} />
        );
      },
      header: 'Content',
    }),
    columnHelper.accessor('sectionGroupId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Section group id',
    }),
    columnHelper.accessor('sectionId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Section id',
    }),
    columnHelper.accessor('discussionId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Discussion id',
    }),
    columnHelper.accessor('messageId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Message id',
    }),
    columnHelper.accessor('parentMessageContent', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Parent Message Content',
    }),
  ];
};

// Custom global filter function
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const globalFilterFn = <TData extends object>(
  row: Row<AdBoardSectionDataFeedDto>,
  columnId: string,
  filterValue: string
): boolean => {
  const { participantName, content, attachmentName, attachmentOpenedAt } =
    row.original;

  const lowerCaseFilterValue = filterValue.toLowerCase();

  return (
    participantName.toLowerCase().includes(lowerCaseFilterValue) ||
    (content?.toLowerCase().includes(lowerCaseFilterValue) ?? false) ||
    (attachmentName?.toLowerCase().includes(lowerCaseFilterValue) ?? false) ||
    (attachmentOpenedAt
      ? !!dateFormatter(attachmentOpenedAt)
          ?.toLowerCase()
          ?.includes(lowerCaseFilterValue)
      : false) ||
    (attachmentOpenedAt
      ? !!timeFormatter(attachmentOpenedAt)
          ?.toLowerCase()
          ?.includes(lowerCaseFilterValue)
      : false)
  );
};

const FeedTable = ({
  selectedGroup,
  selectedActivity,
  selectedDiscussionId,
  searchQuery,
  filterByUserId,
  selectedMessageId,
}: {
  selectedGroup?: AdBoardSectionGroupDto;
  selectedActivity?: SectionDto;
  selectedDiscussionId?: string;
  searchQuery?: string;
  filterByUserId?: string;
  selectedMessageId?: string;
}) => {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { feedData } = useOutletContext<AdBoardOutletContextType>();
  const [feedDataWithSubrows, setFeedDataWithSubrows] = React.useState<
    AdBoardSectionDataFeedDtoWithSubRows[]
  >([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  useEffect(() => {
    const nested = nestAdBoardDataFeedByThread(feedData, !!filterByUserId);

    let filtered = nested;

    // this case happens for participant view threaded message modal
    if (selectedMessageId) {
      // filter to include all messages in the thread, not just the selected message id
      filtered = nested.filter((thread) => {
        const matchesParent = thread.messageId === selectedMessageId;
        const matchesChild = thread.subRows?.some(
          (sub) => sub.messageId === selectedMessageId
        );
        return matchesParent || matchesChild;
      });
    }

    const defaultExpanded = getDefaultExpandedState(
      filtered,
      !!filterByUserId,
      !!selectedMessageId
    );
    setFeedDataWithSubrows(filtered);
    setExpanded(defaultExpanded);
  }, [feedData, filterByUserId, selectedMessageId]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  useEffect(() => {
    const filters = [];
    if (filterByUserId)
      filters.push({
        id: 'userId',
        value: filterByUserId,
      });
    if (selectedGroup?.id)
      filters.push({
        id: 'sectionGroupId',
        value: selectedGroup?.id,
      });
    if (selectedActivity?.id)
      filters.push({
        id: 'sectionId',
        value: selectedActivity?.id,
      });
    if (selectedDiscussionId)
      filters.push({
        id: 'discussionId',
        value: selectedDiscussionId,
      });
    setColumnFilters(filters);
  }, [
    filterByUserId,
    selectedActivity?.id,
    selectedGroup?.id,
    selectedDiscussionId,
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = createColumns(
    filterByUserId,
    selectedMessageId,
    selectedActivity
  );
  // Use the table hook with global filter enabled
  const table = useReactTable({
    data: feedDataWithSubrows,
    columns,
    state: {
      globalFilter: searchQuery,
      columnFilters,
      columnVisibility: {
        userId: false,
        sectionGroupId: false,
        sectionId: false,
        discussionId: false,
        messageId: false,
        parentMessageContent: false,
      },
      pagination,
      expanded,
    },
    globalFilterFn,
    onExpandedChange: setExpanded,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.messageId,
    getSubRows: (row) => row.subRows,
    // do not show other people's messages in participant view, even if the participant has a reply in their thread
    filterFromLeafRows: filterByUserId ? false : true,
  });

  return (
    <TableContainer
      ref={tableRef}
      bg={useColorModeValue('white', 'gray.700')}
      mt={4}
      maxH={'100%'}
      overflowX={'visible'}
      overflowY={'visible'}
    >
      <Table variant="unstyled" data-testid="feed-table">
        <Tbody>
          {table.getExpandedRowModel().rows?.length ? (
            table.getExpandedRowModel().rows.map((row) => (
              <Tr
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                fontSize={'sm'}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    textAlign="left"
                    verticalAlign="top"
                    style={{
                      paddingLeft: '18px',
                      paddingRight: '18px',
                      paddingTop: cell.row.depth < 1 ? '16px' : '8px',
                      paddingBottom: cell.row.depth < 1 ? '16px' : '8px',
                      whiteSpace: 'normal', // Avoids the need to manually sanitize strings (e.g., replace \n or multiple spaces).
                      ...(cell.column.id === 'participantName'
                        ? {
                            width: `${cell.column.getSize()}px`,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }
                        : {}),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))
          ) : (
            <Tr>
              <Td
                colSpan={columns.length}
                textAlign={'center'}
                color={'blue.700'}
                py={20}
              >
                {i18n.t('adBoardDetails.engagement.noData')}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export { FeedTable };
